import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, navigate } from "raviger";
import axios from "axios";
import { Switch } from 'formik-mui';
import { Button, Card, Col, Form, FormControl, InputGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft, faEnvelope,
  faChevronLeft, faChevronRight, faInfoCircle, faMinusSquare, faPen, faUpload, faUserMinus, faUserPlus,
  faKey,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import {
  faCircleE, faCircleI, faCircleO, faCircleT, faCircleV, faHouseMedical, faPencil, faPenSlash, faUserUnlock
} from '@fortawesome/pro-solid-svg-icons';
import { Field, Formik } from "formik";
import * as Yup from 'yup';
import DualListBox from 'react-dual-listbox';
import Moment from 'react-moment';
import Header from "../components/Header";
import { AuthContext } from "./AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import { Checkbox, DropDown, TextInput, ToggleSwitch } from '../components/Form';
import LoadingLink from '../components/LoadingLink';
import ActionsDropdown from '../components/ActionsDropdown';

function UserManagement({ organization }) {

  const { dispatch, state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({users: [], isFetching: false});
  const [filteredData, setFilteredData] = useState({users: [], isFetching: false});

  // const [userToDelete, setUserToDelete] = useState({id: 0, first_name: '', last_name: ''});
  const [showUserConfirm, setShowUserConfirm] = useState(false);
  const handleUserClose = () => setShowUserConfirm(false);

  const [userToReset, setUserToReset] = useState({id: 0, first_name: '', last_name: ''});
  const [showUserResetConfirm, setShowUserResetConfirm] = useState(false);
  const handleUserResetClose = () => setShowUserResetConfirm(false);

  const [newEmailData, setNewEmailData] = useState({id:null, new_email:'', org_slug:organization});
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const handleChangeEmailClose = () => setShowChangeEmail(false);
  const newEmailRef = useRef(null);

  const [file, setFile] = useState({users: [], isFetching: false});
  const [showUploadCSV, setShowUploadCSV] = useState(false);
  const handleUploadCSVClose = () => setShowUploadCSV(false);

  const [timer, setTimer] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showUpdateUsers, setShowUpdateUsers] = useState(false);
  const handleUpdateUsersClose = () => setShowUpdateUsers(false);
  const [showUpdateUsersAccess, setShowUpdateUsersAccess] = useState(false);
  const handleUpdateUsersAccessClose = () => setShowUpdateUsersAccess(false);
  const [showUpdateUsersRemoveAccess, setShowUpdateUsersRemoveAccess] = useState(false);
  const handleUpdateUsersRemoveAccessClose = () => setShowUpdateUsersRemoveAccess(false);
  const [incidents, setIncidents] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showRemoveUsers, setShowRemoveUsers] = useState(false);
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState([]);
  const [trainingSelected, setTrainingSelected] = useState([]);
  const [trainingOptions, setTrainingOptions] = useState([]);

  const handleRemoveUserSubmit = async (ids) => {
    await axios.delete('/accounts/api/user/remove_users/?organization=' + state.organization.id, {data: {'ids':ids}})
    .then(response => {
      setData(prevState => ({ ...prevState, "users":data.users.filter(user => !selectedUsers.includes(user.id)) }));
      setFilteredData(prevState => ({ ...prevState, "users":filteredData.users.filter(user => !selectedUsers.includes(user.id)) }));
      setSelectedUsers([]);
      handleUserClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleResetUserSubmit = async () => {
    await axios.patch('/accounts/api/user/' + userToReset.id + '/reset_password/', {'organization': state.organization.id})
    .then(response => {
      setUserToReset({id: 0, first_name: '', last_name: ''});
      handleUserResetClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleUploadCSV = async () => {
    const formData = new FormData();
    formData.append('user_csv', file);

    await axios.post('/accounts/api/user/upload_csv/?organization=' + organization, formData)
    .then(response => {
      setData(prevState => ({ ...prevState, "users":data.users.concat(response.data)}));
      setFilteredData(prevState => ({ ...prevState, "users":filteredData.users.concat(response.data)}));
      handleUploadCSVClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  // Use a delay for quick search
  function changeDelay(searchTerm) {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    setTimer(
      setTimeout(() => {
        handleSearch(searchTerm);
      }, 1000)
    );
  }

  // Update searchTerm when field input changes.
  const handleSearch = (searchTerm) => {
    if (searchTerm) {
      setFilteredData(prevState => ({ ...prevState,
        "users":data.users.filter(user => (user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  user.cell_phone.toLowerCase().includes(searchTerm.toLowerCase()))
      ) }));
    }
    else {
      setFilteredData(prevState => ({ ...prevState, "users":data.users }));
    }
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    if (state.organization.id) {
      const fetchUserData = async () => {
        setData({users: [], isFetching: true});
        axios.get('/accounts/api/user/?organization=' + state.organization.id, {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setData({users: response.data, isFetching: false});
            setFilteredData({users: response.data, isFetching: false});
          }
        })
        .catch(error => {
          if (!unmounted) {
            setData({users: [], isFetching: false});
            setShowSystemError(true);
          }
        });

        // Cleanup.
        return () => {
          unmounted = true;
          source.cancel();
        };
      }

      fetchUserData();
    }

    const fetchIncidentData = async () => {
      // Fetch Incident data.
      await axios.get('/incident/api/incident/?organization_slug=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          let training_options = [];
          response.data.forEach(inc => {
            // Build incident option list.
            if (!inc.training) {
              options.push({
                value: inc.id,
                label: inc.end_time
                ? `(*) ${inc.name}`
                : inc.name
              });
            }
            else {
              training_options.push({
                value: inc.id,
                label: inc.end_time
                ? `(*) ${inc.name}`
                : inc.name
              });
            }
          });
          setIncidents(response.data);
          setOptions(options);
          setTrainingOptions(training_options);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };

    if (options.length === 0) {
      fetchIncidentData()
    }

  }, [state.organization.id]);

  return (
    <>
    {state.user.is_superuser || state.user.user_perms ?
    <span className="mt-4 ms-auto me-auto" style={{width:"80%", maxWidth:"80%"}}>
    <Header>
      <span style={{cursor:'pointer'}} onClick={() => navigate("/" + organization + '/manage/organization')} className="me-2"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="sm" inverse /></span>
      {state.organization.name} - Manage Users
      <OverlayTrigger
        key={"add-user"}
        placement="bottom"
        overlay={
          <Tooltip id={`tooltip-add-user`}>
            Create new user
          </Tooltip>
        }
      >
        <Link href={"/" + organization + "/accounts/user/new"}><FontAwesomeIcon icon={faUserPlus} size="sm" className="ms-2 fa-move-up" inverse /></Link>
      </OverlayTrigger>
    </Header>
    <InputGroup className="mb-3" style={{fontSize:"24px"}}>
      <FormControl
        type="text"
        placeholder="Quick Search..."
        name="searchTerm"
        onChange={(event) => {
          changeDelay(event.target.value);
        }}
      />
      {/* <Checkbox
        id={'edit-mode'}
        name={'edit-mode'}
        front_label={"Edit Mode"}
        className={"float-end"}
        checked={editMode}
        style={{
          transform:"scale(1.5)",
          marginRight:"1px",
          marginTop:"-5px",
          marginBottom:"-5px",
          paddingLeft:"11px"
        }}
        onChange={() => setEditMode(!editMode)}
      /> */}
      <ActionsDropdown variant="secondary" title={"Actions"} className="float-end" style={{height:"38px", paddingTop:"4px"}}>
        <LoadingLink
          onClick={() => {setEditMode(!editMode)}}
          className="d-block"
          isLoading={false}
          label={(editMode ? "Exit" : "Enter") + " Select Mode"}
          >
          <FontAwesomeIcon icon={editMode ? faPenSlash : faPen} className="fas" inverse />
        </LoadingLink>
        <LoadingLink
          onClick={() => {setShowUpdateUsersAccess(true);}}
          className="d-block"
          isLoading={false}
          label="Grant Incident Access to Selected Users"
          disabled={!editMode || selectedUsers.length === 0}
          disabled_text="Must be in Select Mode and have users selected."
          >
          <FontAwesomeIcon icon={faKey} className="fas" inverse />
        </LoadingLink>
        <LoadingLink
          onClick={() => {setShowUpdateUsersRemoveAccess(true);}}
          className="d-block"
          isLoading={false}
          label="Remove Incident Access from Selected Users"
          disabled={!editMode || selectedUsers.length === 0}
          disabled_text="Must be in Select Mode and have users selected."
          >
            <span className="fa-layers" style={{marginLeft:"2px"}}>
              <FontAwesomeIcon icon={faKey} />
              <FontAwesomeIcon icon={faBan} color="#ef5151" />
            </span>
        </LoadingLink>
        <LoadingLink
          onClick={() => {setShowUpdateUsers(true);}}
          className="d-block"
          isLoading={false}
          label="Update Vet Info for Selected Users"
          disabled={!editMode || selectedUsers.length === 0}
          disabled_text="Must be in Select Mode and have users selected."
          >
          <FontAwesomeIcon icon={faHouseMedical} className="fas" inverse />
        </LoadingLink>
        <LoadingLink
          onClick={() => {setShowUserConfirm(true);}}
          className="d-block"
          isLoading={false}
          label="Remove Selected Users"
          disabled={!editMode || selectedUsers.length === 0}
          disabled_text="Must be in Select Mode and have users selected."
          >
          <FontAwesomeIcon icon={faUserMinus} className="fas" inverse />
        </LoadingLink>
        {/* <LoadingLink
          href={"/" + organization + "/accounts/user/edit/" + user.id}
          className="d-block"
          isLoading={false}
          label="Update User"
          >
          <FontAwesomeIcon icon={faPencil} className="fas" inverse />
        </LoadingLink>
        <LoadingLink
          onClick={() => {setNewEmailData({id:user.id, new_email:'', org_slug:organization});setShowChangeEmail(true);setTimeout(()=>{newEmailRef.current && newEmailRef.current.focus()}, 1);}}
          className="d-block"
          isLoading={false}
          label="Change User Email Address"
          >
          <FontAwesomeIcon icon={faEnvelope} className="fas" inverse />
        </LoadingLink>
        <LoadingLink
          onClick={() => {setUserToDelete({id:user.id, first_name: user.first_name, last_name: user.last_name});setShowUserConfirm(true);}}
          className="d-block"
          isLoading={false}
          label="Remove User"
          >
          <FontAwesomeIcon icon={faMinusSquare} className="fas" inverse />
        </LoadingLink>
        <LoadingLink
          onClick={() => {setUserToReset({id:user.id, first_name: user.first_name, last_name: user.last_name});setShowUserResetConfirm(true);}}
          className="d-block"
          isLoading={false}
          label="Reset User Password"
          >
          <FontAwesomeIcon icon={faUserUnlock} className="fas" inverse />
        </LoadingLink> */}
      </ActionsDropdown>
    </InputGroup>
    <Row className="flex-nowrap">
      <Col style={{minWidth:"50px", maxWidth:"50px", marginLeft:"0px"}}>
          Select
      </Col>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"0px"}}>
          Last Name
      </Col>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"1px"}}>
          First Name
      </Col>
      <Col style={{minWidth:"300px", maxWidth:"300px"}}>
          Email
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px"}}>
          Phone
      </Col>
      <Col style={{paddingRight:"0px", minWidth:"100px", maxWidth:"100px"}}>
          Operational
      </Col>
      <Col style={{paddingRight:"0px", minWidth:"100px", maxWidth:"100px"}}>
          Training
      </Col>
      <Col style={{minWidth:"75px"}}>
          Perms
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
          Actions
      </Col>
    </Row>
    {filteredData.users.filter(user => !user.is_superuser).map(user => (
      <Card key={user.id} className="rounded mb-1" style={{height:"32px", paddingLeft:"11px"}}>
        <div className="row no-gutters flex-nowrap me-0">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}}>
            <Checkbox
              id={'select' + user.id}
              name={'select' + user.id}
              style={{
                transform: "scale(.75)",
                marginRight: "1px",
                marginTop: "-6px",
                marginBottom: "-5px",
                paddingLeft:"13px"
              }}
              disabled={!editMode}
              onChange={() => {
                if (selectedUsers.includes(user.id)) {
                  setSelectedUsers(selectedUsers.filter(user_id => user_id !== user.id));
                }
                else {
                  setSelectedUsers(selectedUsers.concat(user.id));
                }
              }}
            />
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.last_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.first_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"300px", maxWidth:"300px", backgroundColor:"#615e5e"}}>
            {user.email}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}}>
            {user.display_phone}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", backgroundColor:"#615e5e"}}>
            {user.all_operational ? "All" : Object.keys(user.incident_objects.filter(inc => !inc.training)).length === 0 ? "None" : <OverlayTrigger
              key={"active-incidents"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-active-incidents`}>
                  {Object.entries(user.incident_objects.filter(inc => !inc.training)).map(([inc_slug, incident]) => (
                    <span key={inc_slug}>
                      {incident.name}
                      <br />
                    </span>
                  ))}
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
            </OverlayTrigger>}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", backgroundColor:"#615e5e"}}>
            {user.all_training ? "All" : Object.keys(user.incident_objects.filter(inc => inc.training)).length === 0 ? "None" : <OverlayTrigger
              key={"active-incidents"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-active-incidents`}>
                  {Object.entries(user.incident_objects.filter(inc => inc.training)).map(([inc_slug, incident]) => (
                    <span key={inc_slug}>
                      {incident.name}
                      <br />
                    </span>
                  ))}
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
            </OverlayTrigger>}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"75px", backgroundColor:"#615e5e"}}>
            {user.user_perms ? <OverlayTrigger
              key={"org-perms"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-org-perms`}>
                  User is an Organization Admin
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faCircleO} size="lg" className="ms-1" />
            </OverlayTrigger> : ""}
            {user.incident_perms ? <OverlayTrigger
              key={"incident-perms"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-incident-perms`}>
                  User is an Incident Admin
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faCircleI} size="lg" className="ms-1" />
            </OverlayTrigger> : ""}
            {user.vet_perms ? <OverlayTrigger
              key={"vet-perms"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-vet-perms`}>
                  User has veterinary permissions{user.vet_role === 'Veterinarian' ? ' and has the Veterinarian role.' : user.vet_role === 'Vet Tech' ? ' and has the Vet Tech role.' : ''}
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faCircleV} size="lg" className="ms-1" />
            </OverlayTrigger> : ""}
          </Col>
          <Col className="border" style={{height:"32px", paddingRight:"4px", paddingTop:"2px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
            <ActionsDropdown variant="secondary" title={"Actions"} disabled={editMode} className="float-end" style={{height:"26px", paddingTop:"0px"}}>
							<LoadingLink
								href={"/" + organization + "/accounts/user/edit/" + user.id}
								className="d-block"
								isLoading={false}
								label="Update User"
								>
								<FontAwesomeIcon icon={faPencil} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
								onClick={() => {setNewEmailData({id:user.id, new_email:'', org_slug:organization});setShowChangeEmail(true);setTimeout(()=>{newEmailRef.current && newEmailRef.current.focus()}, 1);}}
								className="d-block"
								isLoading={false}
								label="Change User Email Address"
								>
								<FontAwesomeIcon icon={faEnvelope} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
								// onClick={() => {setUserToDelete({id:user.id, first_name: user.first_name, last_name: user.last_name});setShowUserConfirm(true);}}
								onClick={() => {setSelectedUsers([user.id]);setShowUserConfirm(true);}}
                className="d-block"
								isLoading={false}
								label="Remove User"
								>
								<FontAwesomeIcon icon={faMinusSquare} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
								onClick={() => {setUserToReset({id:user.id, first_name: user.first_name, last_name: user.last_name});setShowUserResetConfirm(true);}}
								className="d-block"
								isLoading={false}
								label="Reset User Password"
								>
								<FontAwesomeIcon icon={faUserUnlock} className="fas" inverse />
							</LoadingLink>
						</ActionsDropdown>
          </Col>
        </div>
      </Card>
    ))}
    <br/>
    <Row className="flex-nowrap" style={{marginLeft:"-15px"}}>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"15px", marginRight:"0px", paddingLeft:"0px", paddingRight:"0px"}}>
          Admin Last Name
      </Col>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"3px", marginRight:"0px", paddingLeft:"0px", paddingRight:"0px"}}>
          Admin First Name
      </Col>
      <Col style={{minWidth:"300px", maxWidth:"300px", paddingLeft:"0px"}}>
          Admin Email
      </Col>
      { state.user.is_superuser ?
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
          Actions
      </Col>
      : ''}
    </Row>
    {filteredData.users.filter(user => user.is_superuser).map(user => (
      <Card key={user.id} className="rounded mb-1" style={{height:"32px", paddingLeft:"11px", maxWidth:"540px"}}>
        <div className="row no-gutters flex-nowrap">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.last_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.first_name}
          </Col>
          <Col className="border" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"300px", maxWidth:"300px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
            {user.email}
          </Col>
          { state.user.is_superuser ?
            <Col className="border" style={{height:"32px", paddingRight:"4px", paddingTop:"2px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
              <ActionsDropdown variant="secondary" title={"Actions"} className="float-end" style={{height:"26px", paddingTop:"0px"}}>
                <LoadingLink
                  href={"/" + organization + "/accounts/user/edit/" + user.id}
                  className="d-block"
                  isLoading={false}
                  label="Update User"
                  >
                  <FontAwesomeIcon icon={faPencil} className="fas" inverse />
                </LoadingLink>
              </ActionsDropdown>
            </Col>
          : ''}
        </div>
      </Card>
    ))}
    </span> : ""}
    <Modal show={showUserConfirm} onHide={handleUserClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm User Removal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          Are you sure you would like to remove the following user(s) from the {state.organization.name} Organization? 
        </p>
        {data.users.filter(user => selectedUsers.includes(user.id)).map(user => (
          <div key={user.id}>{user.first_name} {user.last_name}</div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => {handleRemoveUserSubmit(selectedUsers)}}>Yes</Button>
        <Button variant="secondary" onClick={handleUserClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showUserResetConfirm} onHide={handleUserResetClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm User Password Reset</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Are you sure you would like to initialize a password reset for user {userToReset.first_name} {userToReset.last_name}?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleResetUserSubmit}>Yes</Button>
        <Button variant="secondary" onClick={handleUserResetClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showUploadCSV} onHide={handleUploadCSVClose}>
      <Modal.Header closeButton>
        <Modal.Title>Upload User CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="formFile" className="mt-3 mb-3">
          <Form.Control type="file" onChange={(event) => {setFile(event.target.files[0]) }} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleUploadCSV}>Upload</Button>
        <Button variant="secondary" onClick={handleUploadCSVClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Formik
      initialValues={newEmailData}
      enableReinitialize={true}
      validationSchema={Yup.object({
        id: Yup.string().required(),
        new_email: Yup.string().required('Please enter a valid email address.')
          .max(50, 'Must be 50 characters or less')
          .email('Please enter a valid email address.'),
      })}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        // Send verification email.
        axios.get('/accounts/api/user/' + values.id + '/email_verification/', {params:values})
        .then(response => {
          resetForm();
          setNewEmailData({id:null, new_email:'', org_slug:organization});
          setShowChangeEmail(false);
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
    {formikProps => (
      <Modal show={showChangeEmail} onHide={handleChangeEmailClose} aria-modal="true">
        <Modal.Header closeButton>
          <Modal.Title>Change Email Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mt-3">
            <TextInput
              rows={5}
              label="New Email Address"
              name="new_email"
              id="new_email"
              xs="12"
              ref={newEmailRef}
            />
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm();}}>Send Verification Email</Button>
          <Button variant="secondary" onClick={handleChangeEmailClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    )}
    </Formik>
    <Formik
      initialValues={{vet_perms:null, vet_role:null}}
      enableReinitialize={true}
      // validationSchema={Yup.object({
      //   id: Yup.string().required(),
      //   new_email: Yup.string().required('Please enter a valid email address.')
      //     .max(50, 'Must be 50 characters or less')
      //     .email('Please enter a valid email address.'),
      // })}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        // Submit.
        axios.patch('/accounts/api/user/mass_update_users/?organization=' + state.organization.id, {'ids':selectedUsers, ...values})
        .then(response => {
          setData(prevState => ({ ...prevState, "users":prevState.users.map(user => 
            selectedUsers.includes(user.id)
              ? { ...user, vet_perms: [true, false].includes(values.vet_perms) ? values.vet_perms : user.vet_perms, vet_role: values.vet_role || user.vet_role } // Updates only the matched object
              : user // Returns unmatched objects identically
          )}));
          setFilteredData(prevState => ({ ...prevState, "users":prevState.users.map(user => 
            selectedUsers.includes(user.id)
              ? { ...user, vet_perms: [true, false].includes(values.vet_perms) ? values.vet_perms : user.vet_perms, vet_role: values.vet_role || user.vet_role } // Updates only the matched object
              : user // Returns unmatched objects identically
          )}));
          resetForm();
          setShowUpdateUsers(false);
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
    {formikProps => (
      <Modal show={showUpdateUsers} onHide={handleUpdateUsersClose} aria-modal="true">
        <Modal.Header closeButton>
          <Modal.Title>Update Vet Info for Users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs="10">
              <DropDown
                label="Set Veterinary Perms"
                id="vet_perms"
                name="vet_perms"
                type="text"
                isClearable={true}
                options={[{value:true, label:"Add Veterinary Perms"}, {value:false, label:"Remove Veterinary Perms"}]}
                value={formikProps.values.vet_perms||''}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs="10">
              <DropDown
                label="Set Vet Role"
                id="vet_role"
                name="vet_role"
                type="text"
                isClearable={true}
                options={[{value:"Vet Tech", label:"Vet Tech"}, {value:"Veterinarian", label:"Veterinarian"}]}
                value={formikProps.values.vet_role||''}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm();}}>Save</Button>
          <Button variant="secondary" onClick={handleUpdateUsersClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    )}
    </Formik>
    <Formik
      initialValues={{add_all_training:null, add_all_operational:null, training_to_add:[], operational_to_add:[]}}
      enableReinitialize={true}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        // Submit.
        axios.patch('/accounts/api/user/mass_update_users/?organization=' + state.organization.id, {'ids':selectedUsers, ...values})
        .then(response => {
          setData(prevState => ({ ...prevState, "users":prevState.users.map(user => 
            selectedUsers.includes(user.id)
              ? { ...user, all_training: values.add_all_training === true ? values.add_all_training : user.all_training,
                           all_operational: values.add_all_operational === true ? values.add_all_operational : user.all_operational,
                           incident_objects:[...user.incident_objects, ...incidents.filter(incident => (!user.incident_objects.map(incident => incident.id).includes(incident.id)) && (values.training_to_add.includes(incident.id) || values.operational_to_add.includes(incident.id)))]} // Updates only the matched object
              : user // Returns unmatched objects identically
          )}));
          setFilteredData(prevState => ({ ...prevState, "users":prevState.users.map(user => 
            selectedUsers.includes(user.id)
              ? { ...user, all_training: values.add_all_training === true ? values.add_all_training : user.all_training,
                           all_operational: values.add_all_operational === true ? values.add_all_operational : user.all_operational,
                           incident_objects:[...user.incident_objects, ...incidents.filter(incident => (!user.incident_objects.map(incident => incident.id).includes(incident.id)) && (values.training_to_add.includes(incident.id) || values.operational_to_add.includes(incident.id)))]} // Updates only the matched object
              : user // Returns unmatched objects identically
          )}));
          resetForm({add_all_training:null, add_all_operational:null, training_to_add:[], operational_to_add:[]});
          setShowUpdateUsersAccess(false);
          setSelected([]);
          setTrainingSelected([]);
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
    {formikProps => (
      <Modal show={showUpdateUsersAccess} onHide={handleUpdateUsersAccessClose} aria-modal="true">
        <Modal.Header closeButton>
          <Modal.Title>Grant Access to Users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs="10">
              <ToggleSwitch xs="6" name="add_all_training" id="add_all_training" label="Add All Training"
                onChange={(event) => {
                  formikProps.setFieldValue("add_all_training", event.target.checked);
                  if (event.target.checked) {
                    formikProps.setFieldValue("training_to_add", trainingOptions.map(option => option.value));
                    setTrainingSelected(trainingOptions.map(option => option.value));
                  }
                }}
              />
            </Col>
          </Row>
          <label className="mt-1 mb-1">
            Training Incident Access
            <OverlayTrigger
              key={"training-access"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-training-access`}>
                  (*) Indicates a hidden incident.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
            </OverlayTrigger>
          </label>
          <Row style={{marginLeft:"0px"}}>
            <DualListBox
              className="ps-0"
              canFilter
              name="training_to_add"
              options={trainingOptions}
              selected={trainingSelected}
              onChange={(newValue) => {setTrainingSelected(newValue); formikProps.setFieldValue("training_to_add", newValue);}}
              disabled={formikProps.values.add_all_training}
              icons={{
                moveToAvailable: <FontAwesomeIcon icon={faChevronLeft} />,
                moveAllToAvailable: [
                  <FontAwesomeIcon key={0} icon={faChevronLeft} />,
                  <FontAwesomeIcon key={1} icon={faChevronLeft} />,
                ],
                moveToSelected: <FontAwesomeIcon icon={faChevronRight} />,
                moveAllToSelected: [
                  <FontAwesomeIcon key={0} icon={faChevronRight} />,
                  <FontAwesomeIcon key={1} icon={faChevronRight} />,
                ],
                moveDown: <span className="fa fa-chevron-down" />,
                moveUp: <span className="fa fa-chevron-up" />,
                moveTop: <span className="fa fa-double-angle-up" />,
                moveBottom: <span className="fa fa-double-angle-down" />,
              }}
            />
          </Row>
          <Row className="mt-3">
            <Col xs="10">
              <ToggleSwitch xs="6" name="add_all_operational" id="add_all_operational" label="Add All Operational"
                onChange={(event) => {
                  formikProps.setFieldValue("add_all_operational", event.target.checked);
                  if (event.target.checked) {
                    formikProps.setFieldValue("operational_to_add", options.map(option => option.value));
                    setSelected(options.map(option => option.value));
                  }
                }}
                disabled={formikProps.values.incident_perms}
              />
            </Col>
          </Row>
          <label className="mt-1 mb-1">
            Operational Incident Access
            <OverlayTrigger
              key={"operational-access"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-operational-access`}>
                  (*) Indicates a hidden incident.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
            </OverlayTrigger>
          </label>
          <Row style={{marginLeft:"0px"}}>
            <DualListBox
              className="ps-0"
              canFilter
              name="operational_to_add"
              options={options}
              selected={selected}
              onChange={(newValue) => {setSelected(newValue); formikProps.setFieldValue("operational_to_add", newValue);}}
              disabled={formikProps.values.add_all_operational}
              icons={{
                moveToAvailable: <FontAwesomeIcon icon={faChevronLeft} />,
                moveAllToAvailable: [
                  <FontAwesomeIcon key={0} icon={faChevronLeft} />,
                  <FontAwesomeIcon key={1} icon={faChevronLeft} />,
                ],
                moveToSelected: <FontAwesomeIcon icon={faChevronRight} />,
                moveAllToSelected: [
                  <FontAwesomeIcon key={0} icon={faChevronRight} />,
                  <FontAwesomeIcon key={1} icon={faChevronRight} />,
                ],
                moveDown: <span className="fa fa-chevron-down" />,
                moveUp: <span className="fa fa-chevron-up" />,
                moveTop: <span className="fa fa-double-angle-up" />,
                moveBottom: <span className="fa fa-double-angle-down" />,
              }}
            />
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm();}}>Save</Button>
          <Button variant="secondary" onClick={handleUpdateUsersAccessClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    )}
    </Formik>
    <Formik
      initialValues={{remove_all_training:null, remove_all_operational:null, training_to_remove:[], operational_to_remove:[]}}
      enableReinitialize={true}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        // Submit.
        axios.patch('/accounts/api/user/mass_update_users/?organization=' + state.organization.id, {'ids':selectedUsers, ...values})
        .then(response => {
          setData(prevState => ({ ...prevState, "users":prevState.users.map(user => 
            selectedUsers.includes(user.id)
              ? { ...user, all_training: values.remove_all_training === true ? false : user.all_training,
                           all_operational: values.remove_all_operational === true ? false : user.all_operational,
                           incident_objects:user.incident_objects.filter(incident => !values.training_to_remove.includes(incident.id) && !values.operational_to_remove.includes(incident.id))}
              : user // Returns unmatched objects identically
          )}));
          setFilteredData(prevState => ({ ...prevState, "users":prevState.users.map(user => 
            selectedUsers.includes(user.id)
              ? { ...user, all_training: values.remove_all_training === true ? false : user.all_training,
                           all_operational: values.remove_all_operational === true ? false : user.all_operational,
                           incident_objects:user.incident_objects.filter(incident => !values.training_to_remove.includes(incident.id) && !values.operational_to_remove.includes(incident.id))} // Updates only the matched object
              : user // Returns unmatched objects identically
          )}));
          resetForm({remove_all_training:null, remove_all_operational:null, training_to_remove:[], operational_to_remove:[]});
          setShowUpdateUsersRemoveAccess(false);
          setSelected([]);
          setTrainingSelected([]);
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
    {formikProps => (
      <Modal show={showUpdateUsersRemoveAccess} onHide={handleUpdateUsersRemoveAccessClose} aria-modal="true">
        <Modal.Header closeButton>
          <Modal.Title>Remove Access from Users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs="10">
              <ToggleSwitch xs="6" name="remove_all_training" id="remove_all_training" label="Remove All Training"
                onChange={(event) => {
                  formikProps.setFieldValue("remove_all_training", event.target.checked);
                  if (event.target.checked) {
                    formikProps.setFieldValue("training_to_remove", trainingOptions.map(option => option.value));
                    setTrainingSelected(trainingOptions.map(option => option.value));
                  }
                }}
              />
            </Col>
          </Row>
          <label className="mt-1 mb-1">
            Training Incident Access
            <OverlayTrigger
              key={"training-access"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-training-access`}>
                  (*) Indicates a hidden incident.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
            </OverlayTrigger>
          </label>
          <Row style={{marginLeft:"0px"}}>
            <DualListBox
              className="ps-0"
              canFilter
              name="training_to_remove"
              options={trainingOptions}
              selected={trainingSelected}
              onChange={(newValue) => {setTrainingSelected(newValue); formikProps.setFieldValue("training_to_remove", newValue);}}
              disabled={formikProps.values.remove_all_training}
              icons={{
                moveToAvailable: <FontAwesomeIcon icon={faChevronLeft} />,
                moveAllToAvailable: [
                  <FontAwesomeIcon key={0} icon={faChevronLeft} />,
                  <FontAwesomeIcon key={1} icon={faChevronLeft} />,
                ],
                moveToSelected: <FontAwesomeIcon icon={faChevronRight} />,
                moveAllToSelected: [
                  <FontAwesomeIcon key={0} icon={faChevronRight} />,
                  <FontAwesomeIcon key={1} icon={faChevronRight} />,
                ],
                moveDown: <span className="fa fa-chevron-down" />,
                moveUp: <span className="fa fa-chevron-up" />,
                moveTop: <span className="fa fa-double-angle-up" />,
                moveBottom: <span className="fa fa-double-angle-down" />,
              }}
            />
          </Row>
          <Row className="mt-3">
            <Col xs="10">
              <ToggleSwitch xs="6" name="remove_all_operational" id="remove_all_operational" label="Remove All Operational"
                onChange={(event) => {
                  formikProps.setFieldValue("remove_all_operational", event.target.checked);
                  if (event.target.checked) {
                    formikProps.setFieldValue("operational_to_remove", options.map(option => option.value));
                    setSelected(options.map(option => option.value));
                  }
                }}
                disabled={formikProps.values.incident_perms}
              />
            </Col>
          </Row>
          <label className="mt-1 mb-1">
            Operational Incident Access
            <OverlayTrigger
              key={"operational-access"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-operational-access`}>
                  (*) Indicates a hidden incident.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
            </OverlayTrigger>
          </label>
          <Row style={{marginLeft:"0px"}}>
            <DualListBox
              className="ps-0"
              canFilter
              name="operational_to_remove"
              options={options}
              selected={selected}
              onChange={(newValue) => {setSelected(newValue); formikProps.setFieldValue("operational_to_remove", newValue);}}
              disabled={formikProps.values.remove_all_operational}
              icons={{
                moveToAvailable: <FontAwesomeIcon icon={faChevronLeft} />,
                moveAllToAvailable: [
                  <FontAwesomeIcon key={0} icon={faChevronLeft} />,
                  <FontAwesomeIcon key={1} icon={faChevronLeft} />,
                ],
                moveToSelected: <FontAwesomeIcon icon={faChevronRight} />,
                moveAllToSelected: [
                  <FontAwesomeIcon key={0} icon={faChevronRight} />,
                  <FontAwesomeIcon key={1} icon={faChevronRight} />,
                ],
                moveDown: <span className="fa fa-chevron-down" />,
                moveUp: <span className="fa fa-chevron-up" />,
                moveTop: <span className="fa fa-double-angle-up" />,
                moveBottom: <span className="fa fa-double-angle-down" />,
              }}
            />
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm();}}>Save</Button>
          <Button variant="secondary" onClick={handleUpdateUsersRemoveAccessClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    )}
    </Formik>
    </>
  )
}

export default UserManagement
